import { useCallback, useMemo, useRef, useState } from 'react';
import type {
  FieldApi,
  FormApi,
  FormErrors,
  FormValues,
  ValidateOn,
  Validator,
  ValidatorResult,
} from './types.js';

export interface UseFormStateOptions {
  defaultValues?: FormValues;
  controlledValues?: FormValues;
  onValuesChange?: (values: FormValues) => void;
  validateOn?: ValidateOn;
  onSubmit?: (values: FormValues) => void | Promise<void>;
  formValidate?: (values: FormValues) => FormErrors | Promise<FormErrors>;
}

async function runValidators(
  value: unknown,
  validators: ReadonlyArray<Validator>,
  allValues: FormValues,
): Promise<string | string[] | undefined> {
  for (const v of validators) {
    const result: ValidatorResult = await v(value, allValues);
    if (result !== true) return result;
  }
  return undefined;
}

/** Internal state engine behind `<Form>`. Exposed as a hook so consumers can
 *  drive a form programmatically (imperative setters, custom submit wiring)
 *  without wrapping in a component. */
export function useFormState(options: UseFormStateOptions): FormApi {
  const {
    defaultValues,
    controlledValues,
    onValuesChange,
    validateOn = 'blur',
    onSubmit,
    formValidate,
  } = options;

  const isControlled = controlledValues !== undefined;
  const [internalValues, setInternalValues] = useState<FormValues>(defaultValues ?? {});
  const values = isControlled ? controlledValues : internalValues;

  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouchedState] = useState<Record<string, boolean>>({});
  const [pending, setPending] = useState<Record<string, boolean>>({});
  const [submitting, setSubmitting] = useState(false);
  const validatorsRef = useRef<Map<string, ReadonlyArray<Validator>>>(new Map());
  const asyncTicketRef = useRef<Map<string, number>>(new Map());

  const setValues = useCallback(
    (next: FormValues): void => {
      if (!isControlled) setInternalValues(next);
      onValuesChange?.(next);
    },
    [isControlled, onValuesChange],
  );

  const setFieldValue = useCallback(
    (name: string, value: unknown): void => {
      setValues({ ...values, [name]: value });
    },
    [setValues, values],
  );

  const setFieldError = useCallback((name: string, error: string | string[] | undefined): void => {
    setErrors((prev) => ({ ...prev, [name]: error }));
  }, []);

  const setTouched = useCallback((name: string, next = true): void => {
    setTouchedState((prev) => ({ ...prev, [name]: next }));
  }, []);

  const validateField = useCallback(
    async (
      name: string,
      candidateValues: FormValues = values,
    ): Promise<string | string[] | undefined> => {
      const v = validatorsRef.current.get(name);
      if (v === undefined || v.length === 0) return undefined;
      const ticket = (asyncTicketRef.current.get(name) ?? 0) + 1;
      asyncTicketRef.current.set(name, ticket);
      setPending((p) => ({ ...p, [name]: true }));
      const result = await runValidators(candidateValues[name], v, candidateValues);
      if (asyncTicketRef.current.get(name) !== ticket) return undefined;
      setPending((p) => ({ ...p, [name]: false }));
      setFieldError(name, result);
      return result;
    },
    [setFieldError, values],
  );

  const validate = useCallback(
    async (name?: string, candidateValues: FormValues = values): Promise<FormErrors> => {
      if (name !== undefined) {
        const e = await validateField(name, candidateValues);
        return { [name]: e };
      }
      const next: FormErrors = {};
      const names = Array.from(validatorsRef.current.keys());
      await Promise.all(
        names.map(async (n) => {
          next[n] = await validateField(n, candidateValues);
        }),
      );
      if (formValidate !== undefined) {
        const extra = await formValidate(candidateValues);
        Object.assign(next, extra);
      }
      setErrors(next);
      return next;
    },
    [formValidate, validateField, values],
  );

  const reset = useCallback(
    (nextValues: FormValues = defaultValues ?? {}): void => {
      setValues(nextValues);
      setErrors({});
      setTouchedState({});
      setPending({});
    },
    [defaultValues, setValues],
  );

  const submit = useCallback(async (submittedValues?: FormValues): Promise<void> => {
    const candidateValues =
      submittedValues === undefined ? values : { ...values, ...submittedValues };
    if (submittedValues !== undefined) setValues(candidateValues);
    setSubmitting(true);
    try {
      // Submit marks every registered field touched so inline errors can
      // surface even for fields the user never blurred.
      const names = Array.from(validatorsRef.current.keys());
      const allTouched: Record<string, boolean> = {};
      for (const n of names) allTouched[n] = true;
      setTouchedState((prev) => ({ ...prev, ...allTouched }));
      const e = await validate(undefined, candidateValues);
      const hasError = Object.values(e).some((err) => err !== undefined);
      if (hasError) return;
      await onSubmit?.(candidateValues);
    } finally {
      setSubmitting(false);
    }
  }, [onSubmit, setValues, validate, values]);

  const getField = useCallback(
    (name: string): FieldApi => ({
      name,
      value: values[name],
      error: errors[name],
      touched: touched[name] ?? false,
      dirty: values[name] !== (defaultValues?.[name] ?? undefined),
      pending: pending[name] ?? false,
      setValue: (next) => {
        setFieldValue(name, next);
        if (validateOn === 'change') void validateField(name);
      },
      setTouched: (next = true) => {
        setTouched(name, next);
        if (next && validateOn === 'blur') void validateField(name);
      },
      validate: async () => validateField(name),
    }),
    [
      defaultValues,
      errors,
      pending,
      setFieldValue,
      setTouched,
      touched,
      validateField,
      validateOn,
      values,
    ],
  );

  const registerValidators = useCallback(
    (name: string, list: ReadonlyArray<Validator> | undefined): void => {
      if (list === undefined || list.length === 0) validatorsRef.current.delete(name);
      else validatorsRef.current.set(name, list);
    },
    [],
  );

  const api = useMemo<FormApi>(
    () => ({
      values,
      errors,
      submitting,
      getField,
      setFieldValue,
      setFieldError,
      setTouched,
      validate,
      reset,
      submit,
    }),
    [
      errors,
      getField,
      reset,
      setFieldError,
      setFieldValue,
      setTouched,
      submit,
      submitting,
      validate,
      values,
    ],
  );

  // Escape hatch for FormField to wire up its validators.
  (api as unknown as { __registerValidators: typeof registerValidators }).__registerValidators =
    registerValidators;

  return api;
}

export function getRegisterValidators(
  api: FormApi,
): (name: string, list: ReadonlyArray<Validator> | undefined) => void {
  return (
    api as unknown as {
      __registerValidators: (name: string, list: ReadonlyArray<Validator> | undefined) => void;
    }
  ).__registerValidators;
}
